import React, { useCallback, useState } from "react";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { requestIdentityVerification } from "@lib/authentication";
import {
    CodeVerification,
    CodeVerificationProps,
} from "@components/auth/CodeVerification/CodeVerification";
import { useCreateWorkspaceReceiptSenderPhoneNumber } from "@api/workspaceReceiptSender.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useRefetchAllTaskQueries } from "@api/task.api";
import { AddPhoneNumberSenderForm } from "./AddPhoneNumberSenderForm";

interface AddPhoneNumberSenderModalProps {
    show: boolean;
    onClose: () => void;
}

export const AddPhoneNumberSenderModal: React.FC<
    AddPhoneNumberSenderModalProps
> = ({ show, onClose }) => {
    const [phoneNumber, setPhoneNumber] = useState<string>();
    const createSender = useCreateWorkspaceReceiptSenderPhoneNumber();
    const { toast, error } = useToaster();
    const invalidateAllTaskQueries = useRefetchAllTaskQueries();

    const requestVerification: CodeVerificationProps["request"] =
        useCallback(async () => {
            if (!phoneNumber) {
                throw new Error("No phone number provided");
            }
            await requestIdentityVerification({
                phoneOrEmail: phoneNumber,
            });
        }, [phoneNumber]);

    const handleVerificationSuccess: CodeVerificationProps["onSuccess"] =
        useCallback(
            async (verificationCode: string) => {
                if (!phoneNumber) {
                    return;
                }

                try {
                    await createSender.mutateAsync({
                        phoneNumber,
                        verificationCode,
                    });
                    await invalidateAllTaskQueries();
                    toast("Receipt sender added");
                    setPhoneNumber(undefined);
                    onClose();
                } catch (err: any) {
                    const message =
                        err?.body?.message || "Failed to add receipt sender";
                    error(message);
                }
            },
            [
                phoneNumber,
                createSender,
                toast,
                error,
                onClose,
                invalidateAllTaskQueries,
            ],
        );

    const handlePhoneNumberSubmit = useCallback(
        (submittedPhoneNumber: string) => {
            setPhoneNumber(submittedPhoneNumber);
        },
        [],
    );

    const handleClose = useCallback(() => {
        setPhoneNumber(undefined);
        onClose();
    }, [onClose]);

    return (
        <PlainModal show={show} onHide={handleClose}>
            <StandardModalHeader>
                {phoneNumber ? "Verify Phone Number" : "Add Phone Number"}
            </StandardModalHeader>

            {phoneNumber ? (
                <CodeVerification
                    phoneOrEmail={phoneNumber}
                    onSuccess={handleVerificationSuccess}
                    request={requestVerification}
                />
            ) : (
                <AddPhoneNumberSenderForm
                    onSubmit={handlePhoneNumberSubmit}
                    onCancel={handleClose}
                />
            )}
        </PlainModal>
    );
};
